import type { ReactElement, ReactNode } from 'react';
import type { MeetingDetail, Question, Risk } from '@contracts/meetings';
import { useState } from 'react';
import { Button, Empty, Field, Grade, MenuButton, Pill, Rows, TextArea, useNotices } from '../ui';
import {
  useRecordActions,
  useRefuseSeries,
  useSeriesSuggestion,
  useUpdateMeeting,
} from '../api/queries';
import { ago } from '../lib/time';

/**
 * A meeting has five records, not one (PRD 54).
 *
 * Vladimir: *"I want to add risks that were highlighted in the meeting to the
 * meeting. And I want to split meeting notes, action items, risks, decisions.
 * Not everything in one meeting note"* — then, better: *"why don't we add
 * actions, decisions, risks and questions to the meeting as well"*.
 *
 * ## Four sections, not four more tabs
 *
 * The meeting page already carries six tabs. Four more would be ten, and a row
 * of ten tabs is a row nobody reads — the split would have cost more than it
 * bought. So the records are **sections**, each with its own kind, its own
 * count and its own actions. What "split" has to mean is that a risk is a risk
 * rather than a sentence in a paragraph; it does not have to mean a tab.
 *
 * ## Every kind is tagged, and an empty one still speaks
 *
 * ⚠ A missing section reads as "there were none" and as "the product did not
 * look" at exactly the same time. **"No risks were raised" is a fact**, and a
 * meeting where nobody voiced a worry is worth knowing about — so an empty
 * section says so rather than disappearing.
 */
export type RecordKind = 'action' | 'decision' | 'risk' | 'question';

/** One vocabulary for the four kinds, so nothing invents a second one. */
export const KINDS: Record<RecordKind, { label: string; tone: string }> = {
  action: { label: 'Action', tone: 'kind-action' },
  decision: { label: 'Decision', tone: 'kind-decision' },
  risk: { label: 'Risk', tone: 'kind-risk' },
  question: { label: 'Question', tone: 'kind-question' },
};

/** The uppercase tag the screenshots read by — the kind, said once, in colour. */
export function KindTag({ kind }: { kind: RecordKind }): ReactElement {
  const k = KINDS[kind];
  return <span className={`kind-tag ${k.tone}`}>{k.label}</span>;
}

/** A record section: its kind, how many, and what to say when there are none. */
export function RecordSection({
  id,
  kind,
  title,
  count,
  nothing,
  aside,
  children,
}: {
  /** Where "What it produced" lands when its row for this kind is pressed. */
  id?: string;
  kind: RecordKind;
  title: string;
  count: number;
  nothing: string;
  aside?: ReactNode;
  children?: ReactNode;
}): ReactElement {
  // ⚠ Its own header rather than `Card`'s `title`: that prop is intersected
  // with `HTMLAttributes.title`, which is a string, so a tag cannot go in it.
  return (
    <section id={id} className="card card-flush">
      <header className="row" style={{ minHeight: 56 }}>
        <h2 className="t-heading cluster" style={{ gap: 10 }}>
          <KindTag kind={kind} />
          {title}
          <span className="count-badge">{count}</span>
        </h2>
        <span className="grow" />
        {aside}
      </header>
      {count === 0 ? <Empty tone="ok" lead={nothing} /> : children}
    </section>
  );
}

/**
 * The controls every record carries: reword it, say what became of it, or
 * take it away (PRD 54).
 *
 * ⚠ **Delete is not a status, and the menu says so.** A risk that was raised
 * and accepted is a status; a risk the model invented is a deletion, because a
 * record of something that never happened is noise in a list people have to
 * trust. Vladimir asked for both in one sentence — *"change/delete … as they
 * can be wrongly created"* — and the difference is the whole of it.
 */
export function RecordControls({
  kind,
  id,
  statuses,
  onEdit,
}: {
  kind: 'risks' | 'questions' | 'decisions';
  id: string;
  /** What this kind can become, said as a person would. Empty for a decision. */
  statuses: Array<{ label: string; value: string; current: boolean }>;
  onEdit: () => void;
}): ReactElement {
  const { setStatus, remove } = useRecordActions();
  const { show } = useNotices();
  const noun = kind === 'risks' ? 'risk' : kind === 'questions' ? 'question' : 'decision';
  return (
    <MenuButton
      size="sm"
      variant="quiet"
      label="···"
      align="right"
      items={[
        { label: 'Reword it…', sub: 'fix what the meeting was heard to say', onSelect: onEdit },
        ...statuses.map((s) => ({
          label: s.label,
          current: s.current,
          ...(s.current
            ? {}
            : {
                onSelect: () =>
                  setStatus.mutate(
                    { kind: kind as 'risks' | 'questions', id, status: s.value },
                    {
                      onSuccess: () =>
                        show({ tone: 'success', text: `Marked ${s.label.toLowerCase()}.` }),
                      onError: (error) => show({ tone: 'failure', text: error.message }),
                    },
                  ),
              }),
        })),
        {
          label: `Not a ${noun} — delete it`,
          sub: 'nobody said this; it should not be here',
          danger: true,
          onSelect: () =>
            remove.mutate(
              { kind, id },
              {
                onSuccess: () => show({ tone: 'success', text: `Deleted — it was not a ${noun}.` }),
                onError: (error) => show({ tone: 'failure', text: error.message }),
              },
            ),
        },
      ]}
    />
  );
}

/** Rewording in place: a textarea, because a record is a sentence and often two. */
export function RecordEditor({
  kind,
  id,
  text,
  detail,
  onClose,
}: {
  kind: 'risks' | 'questions' | 'decisions';
  id: string;
  text: string;
  detail: string | null;
  onClose: () => void;
}): ReactElement {
  const { edit } = useRecordActions();
  const { show } = useNotices();
  const [draft, setDraft] = useState(text);
  const [why, setWhy] = useState(detail ?? '');
  const save = () => {
    const next = draft.trim();
    if (next.length < 3) return onClose();
    edit.mutate(
      {
        kind,
        id,
        body: { text: next, ...(kind === 'decisions' ? {} : { detail: why.trim() }) },
      },
      {
        onSuccess: () => {
          onClose();
          show({ tone: 'success', text: 'Reworded.' });
        },
        onError: (error) => show({ tone: 'failure', text: error.message }),
      },
    );
  };
  return (
    <div className="stack" style={{ gap: 6, width: '100%' }}>
      <TextArea
        aria-label="What was said"
        value={draft}
        rows={2}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') save();
        }}
      />
      {kind === 'decisions' ? null : (
        <Field
          aria-label="What it threatens"
          placeholder="What it threatens, or what depends on it — optional"
          value={why}
          onChange={(e) => setWhy(e.target.value)}
        />
      )}
      <div className="cluster" style={{ gap: 8 }}>
        <Button
          size="sm"
          variant="primary"
          onClick={save}
          working={edit.isPending ? 'Saving…' : false}
        >
          Save
        </Button>
        <Button size="sm" variant="quiet" onClick={onClose}>
          Cancel
        </Button>
        <span className="mono">⌘↵ saves · Esc cancels</span>
      </div>
    </div>
  );
}

/**
 * The risks a meeting raised.
 *
 * ⚠ A risk that has HAPPENED is not removed and not hidden. It is the most
 * valuable line this product can hold — the thing somebody named before it
 * cost anything, and it arrived anyway — so it stays, marked.
 */
export function Risks({
  meeting,
  onJump,
}: {
  meeting: MeetingDetail;
  onJump: (segmentId: string | null) => void;
}): ReactElement {
  const open = meeting.risks.filter((r) => r.status === 'OPEN').length;
  return (
    <RecordSection
      id="record-risks"
      kind="risk"
      title="What could go wrong"
      count={meeting.risks.length}
      nothing="No risks were raised in this meeting."
      aside={
        meeting.risks.length ? (
          <span className="mono">{open} still open</span>
        ) : (
          <span className="mono">nobody voiced a worry</span>
        )
      }
    >
      <Rows>
        {meeting.risks.map((r) => (
          <RiskRow key={r.id} risk={r} onJump={onJump} canEdit={meeting.access.canEdit} />
        ))}
      </Rows>
    </RecordSection>
  );
}

function RiskRow({
  risk,
  onJump,
  canEdit,
}: {
  risk: Risk;
  onJump: (segmentId: string | null) => void;
  canEdit: boolean;
}): ReactElement {
  const [editing, setEditing] = useState(false);
  const ended = risk.status !== 'OPEN';
  if (editing) {
    return (
      <div className="row stacked record">
        <RecordEditor
          kind="risks"
          id={risk.id}
          text={risk.text}
          detail={risk.detail}
          onClose={() => setEditing(false)}
        />
      </div>
    );
  }
  return (
    <div className={`row stacked record ${ended ? 'is-ended' : ''}`}>
      <div className="row-main">
        <span className="row-title">{risk.text}</span>
        {risk.detail ? <p className="record-detail">{risk.detail}</p> : null}
        <span className="row-sub">
          <RiskState status={risk.status} />
          <Grade value={risk.grade} />
          {risk.span.startSegmentId ? (
            <button
              type="button"
              className="linkish mono"
              onClick={() => onJump(risk.span.startSegmentId)}
            >
              where this was said
            </button>
          ) : null}
          <span className="mono">{ago(risk.createdAt)}</span>
        </span>
      </div>
      {canEdit ? (
        <div className="row-actions">
          <RecordControls
            kind="risks"
            id={risk.id}
            onEdit={() => setEditing(true)}
            statuses={[
              { label: 'Still open', value: 'OPEN', current: risk.status === 'OPEN' },
              { label: 'Accepted', value: 'ACCEPTED', current: risk.status === 'ACCEPTED' },
              { label: 'Avoided', value: 'AVOIDED', current: risk.status === 'AVOIDED' },
              { label: 'It happened', value: 'HAPPENED', current: risk.status === 'HAPPENED' },
            ]}
          />
        </div>
      ) : null}
    </div>
  );
}

function RiskState({ status }: { status: Risk['status'] }): ReactElement {
  if (status === 'HAPPENED') return <Pill tone="red">it happened</Pill>;
  if (status === 'AVOIDED') return <Pill tone="green">avoided</Pill>;
  if (status === 'ACCEPTED') return <Pill>accepted</Pill>;
  return <Pill tone="amber">open</Pill>;
}

/**
 * The questions a meeting left unanswered.
 *
 * ⚠ An answered one keeps **where** the answer is, not a copy of it: the
 * answer is in a transcript and that transcript is the truth. A sentence
 * copied here would be a second version of it, free to drift.
 */
export function Questions({
  meeting,
  onJump,
}: {
  meeting: MeetingDetail;
  onJump: (segmentId: string | null) => void;
}): ReactElement {
  const open = meeting.questions.filter((q) => q.status === 'OPEN');
  const settled = meeting.questions.filter((q) => q.status !== 'OPEN');
  return (
    <RecordSection
      id="record-questions"
      kind="question"
      title="What nobody answered"
      count={meeting.questions.length}
      nothing="Nothing was left hanging."
      aside={
        meeting.questions.length ? (
          <span className="mono">
            {open.length} still open{settled.length ? ` · ${settled.length} settled` : ''}
          </span>
        ) : (
          <span className="mono">every question got an answer</span>
        )
      }
    >
      <Rows>
        {[...open, ...settled].map((q) => (
          <QuestionRow key={q.id} question={q} onJump={onJump} canEdit={meeting.access.canEdit} />
        ))}
      </Rows>
    </RecordSection>
  );
}

function QuestionRow({
  question,
  onJump,
  canEdit,
}: {
  question: Question;
  onJump: (segmentId: string | null) => void;
  canEdit: boolean;
}): ReactElement {
  const [editing, setEditing] = useState(false);
  const ended = question.status !== 'OPEN';
  if (editing) {
    return (
      <div className="row stacked record">
        <RecordEditor
          kind="questions"
          id={question.id}
          text={question.text}
          detail={question.detail}
          onClose={() => setEditing(false)}
        />
      </div>
    );
  }
  return (
    <div className={`row stacked record ${ended ? 'is-ended' : ''}`}>
      <div className="row-main">
        <span className="row-title">{question.text}</span>
        {question.detail ? <p className="record-detail">{question.detail}</p> : null}
        <span className="row-sub">
          {question.status === 'OPEN' ? (
            <Pill tone="amber">open</Pill>
          ) : question.status === 'DROPPED' ? (
            <Pill>dropped</Pill>
          ) : (
            <Pill tone="green">answered</Pill>
          )}
          <Grade value={question.grade} />
          {question.span.startSegmentId ? (
            <button
              type="button"
              className="linkish mono"
              onClick={() => onJump(question.span.startSegmentId)}
            >
              where this was asked
            </button>
          ) : null}
          <span className="mono">{ago(question.createdAt)}</span>
        </span>
      </div>
      {canEdit ? (
        <div className="row-actions">
          <RecordControls
            kind="questions"
            id={question.id}
            onEdit={() => setEditing(true)}
            statuses={[
              { label: 'Still open', value: 'OPEN', current: question.status === 'OPEN' },
              { label: 'Answered', value: 'ANSWERED', current: question.status === 'ANSWERED' },
              {
                label: 'Nobody needs it',
                value: 'DROPPED',
                current: question.status === 'DROPPED',
              },
            ]}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * "This meeting looks like one of those" (PRD 53).
 *
 * A series has subjects; this meeting has subjects; when they are the same
 * subjects **and** the same people, the calendar probably just failed to mark
 * it as an instance. Offering to fix that is worth a banner — but only a
 * banner that says WHY.
 *
 * ⚠ **It names its reasons.** "This looks like Harbor weekly" asks to be
 * trusted; "you talked about Harbor renewal, and 3 of its regulars were here"
 * can be judged in a second, and judged wrong by the reader when it is wrong.
 * A suggestion nobody can check is one people either accept blindly or
 * dismiss blindly, and both are worse than not asking.
 *
 * ⚠ **No is remembered.** A banner that returns after being dismissed teaches
 * people to dismiss without reading, which costs the good suggestion its
 * audience too.
 */
export function SeriesSuggestion({ meeting }: { meeting: MeetingDetail }): ReactElement | null {
  // ⚠ Only asked for a meeting that has happened, is in no series, and belongs
  // to somebody who could file it. Offering to file a meeting that has not
  // started is offering to guess (PRD 53) — and the question costs a read, so
  // it is not asked where the answer could never be shown.
  const worthAsking =
    meeting.seriesKey === null && meeting.captureHealth !== 'SCHEDULED' && meeting.access.canEdit;
  const { data: suggestion } = useSeriesSuggestion(meeting.id, worthAsking);
  const update = useUpdateMeeting(meeting.id);
  const refuse = useRefuseSeries();
  const { show } = useNotices();
  if (!suggestion) return null;

  const { topics, people } = suggestion.because;
  return (
    <div className="band">
      <div className="stack" style={{ gap: 4 }}>
        <span className="t-ui">
          This looks like <strong>{suggestion.title}</strong>.
        </span>
        <span className="mono">
          {topics.length ? `you talked about ${topics.slice(0, 2).join(' and ')}` : 'same subjects'}
          {people ? ` · ${people} of its regulars were here` : ''}
        </span>
      </div>
      <div className="cluster" style={{ gap: 8 }}>
        <Button
          size="sm"
          variant="quiet"
          onClick={() =>
            refuse.mutate(
              { id: meeting.id, seriesKey: suggestion.seriesKey },
              { onError: (e) => show({ tone: 'failure', text: e.message }) },
            )
          }
        >
          Not this one
        </Button>
        <Button
          size="sm"
          variant="primary"
          working={update.isPending ? 'Filing…' : false}
          onClick={() =>
            update.mutate(
              { seriesKey: suggestion.seriesKey, seriesTitle: suggestion.title },
              {
                onSuccess: () =>
                  show({
                    tone: 'success',
                    text: `Filed into ${suggestion.title} — its settings apply from now on.`,
                  }),
                onError: (e) => show({ tone: 'failure', text: e.message }),
              },
            )
          }
        >
          Yes, add it
        </Button>
      </div>
    </div>
  );
}
